'use client'

import React, { createContext, useContext, useMemo, ReactNode } from 'react'

interface DisplayNamesContextType {
  get: (userId: number | null | undefined, fallback?: string) => string
}

const DisplayNamesContext = createContext<DisplayNamesContextType>({
  get: (_, fallback) => fallback ?? '',
})

interface DisplayNamesProviderProps {
  children: ReactNode
  /** Pre-built userId → displayName map from /agency-memberships/display-names */
  displayNameMap?: Record<string, string>
}

export function DisplayNamesProvider({ children, displayNameMap }: DisplayNamesProviderProps) {
  const map = useMemo(() => {
    const m = new Map<number, string>()

    if (displayNameMap) {
      for (const [userId, name] of Object.entries(displayNameMap)) {
        if (name) {
          m.set(Number(userId), name)
        }
      }
    }

    return m
  }, [displayNameMap])

  const get = useMemo(
    () =>
      (userId: number | null | undefined, fallback?: string): string => {
        if (userId != null) {
          const name = map.get(userId)
          if (name) return name
        }
        return fallback ?? ''
      },
    [map]
  )

  const value = useMemo(() => ({ get }), [get])

  return (
    <DisplayNamesContext.Provider value={value}>
      {children}
    </DisplayNamesContext.Provider>
  )
}

export function useDisplayNames() {
  return useContext(DisplayNamesContext)
}
